// src/services/crewPresence.ts
// Lifecycle-driven Crew presence for large concurrent user counts.
//
// Design:
// - One canonical presence document per user: userPresence/{uid}
// - One writer per active app session (MapView)
// - Presence is lifecycle-driven: online while foregrounded, offline while hidden/backgrounded
// - No recurring heartbeat writes; clean offline writes are best-effort
// - Accepted-crew visibility is cached instead of rebuilt on every presence publish
// - Viewers use one Firestore query listener instead of polling every crew doc
// - Viewers react to Firestore presence changes without polling

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import type { CrewPresence } from "../types";

export const CREW_PRESENCE_MIN_MOVE_METERS = 75;

// Presence is event-driven. Readers locally expire a stale online marker if a
// crash/force-kill prevents the client from sending a clean offline transition.
export const CREW_PRESENCE_STALE_MS = 60 * 1000;
const CREW_PRESENCE_STALE_RECHECK_MS = 15 * 1000;

const CREW_VISIBILITY_CACHE_MS = 5 * 60 * 1000;

const visibilityCache = new Map<
  string,
  { expiresAt: number; visibleTo: Record<string, boolean> }
>();

type LastWriteState = {
  at: number;
  lat: number | null;
  lng: number | null;
  online: boolean;
  visibilityKey: string;
  identityKey: string;
};

const lastWriteByUid = new Map<string, LastWriteState>();
const writeInFlightByUid = new Map<string, Promise<void>>();

function enqueueCrewPresenceWrite(
  uid: string,
  task: () => Promise<void>,
): Promise<void> {
  const previous = writeInFlightByUid.get(uid) || Promise.resolve();
  const next = previous
    .catch(() => undefined)
    .then(task);

  writeInFlightByUid.set(uid, next);
  return next.finally(() => {
    if (writeInFlightByUid.get(uid) === next) {
      writeInFlightByUid.delete(uid);
    }
  });
}

const isCrewPresenceDebugEnabled = () => {
  try {
    return (
      import.meta.env.DEV ||
      window.localStorage.getItem("roddo.debug.crewPresence") === "1"
    );
  } catch {
    return false;
  }
};

function presenceDebug(message: string, detail?: Record<string, unknown>) {
  if (!isCrewPresenceDebugEnabled()) return;
  console.info(`[crewPresence] ${message}`, detail || {});
}

export function isCrewPresenceOptedIn(settings: any): boolean {
  return (
    settings?.shareCrewMapPresence === true ||
    settings?.crewMapPresence === true ||
    settings?.crewMapPresenceEnabled === true
  );
}

function nowMs() {
  return Date.now();
}

function safeString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function safeNumber(value: unknown, fallback = 0): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function toMillis(value: any, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value && typeof value.toMillis === "function") {
    try {
      return safeNumber(value.toMillis(), fallback);
    } catch {
      return fallback;
    }
  }
  if (value && typeof value.toDate === "function") {
    try {
      const date = value.toDate();
      return date instanceof Date
        ? safeNumber(date.getTime(), fallback)
        : fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

function distanceMeters(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const radius = 6371e3;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) *
      Math.cos(phi2) *
      Math.sin(deltaLambda / 2) *
      Math.sin(deltaLambda / 2);
  return radius * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

function normalizePresence(id: string, data: any): CrewPresence {
  const lat = Number(data?.approxLocation?.lat ?? data?.location?.lat);
  const lng = Number(
    data?.approxLocation?.lng ??
      data?.approxLocation?.lon ??
      data?.location?.lng ??
      data?.location?.lon,
  );
  const lastSeenAt = toMillis(
    data?.lastSeenAt,
    safeNumber(data?.lastSeenAt, 0),
  );
  const isOnline = data?.isOnline === true || data?.online === true;

  return {
    uid: safeString(data?.uid, id),
    displayName: safeString(data?.displayName, "Crewmate"),
    avatarUrl: safeString(data?.avatarUrl || data?.avatar, ""),
    isOnline,
    online: isOnline,
    locationSharing: data?.locationSharing === "crew" ? "crew" : "off",
    lastSeenAt,
    heartbeatAt: toMillis(data?.heartbeatAt, lastSeenAt),
    updatedAt: toMillis(data?.updatedAt, lastSeenAt),
    approxLocation:
      Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : undefined,
  };
}

function visibilityKey(visibleTo: Record<string, boolean>): string {
  return Object.keys(visibleTo).filter(Boolean).sort().join("|");
}

async function resolveAcceptedCrewVisibleTo(
  uid: string,
  forceRefresh = false,
): Promise<Record<string, boolean>> {
  const currentUid = String(uid || "").trim();
  if (!currentUid) return {};

  const cached = visibilityCache.get(currentUid);
  if (!forceRefresh && cached && cached.expiresAt > nowMs()) {
    return cached.visibleTo;
  }

  const visibleTo: Record<string, boolean> = {};
  const seen = new Set<string>();

  const addRelationship = (data: any) => {
    if (String(data?.status || "") !== "accepted") return;

    const requesterId = safeString(data?.requesterId).trim();
    const recipientId = safeString(data?.recipientId).trim();
    const participantIds = Array.isArray(data?.participantIds)
      ? data.participantIds
          .map((id: unknown) => String(id || "").trim())
          .filter(Boolean)
      : [];

    const ids = Array.from(
      new Set([...participantIds, requesterId, recipientId].filter(Boolean)),
    );

    ids.forEach((id) => {
      if (id !== currentUid) visibleTo[id] = true;
    });
  };

  const run = async (relationshipQuery: ReturnType<typeof query>) => {
    const snapshot = await getDocs(relationshipQuery);
    snapshot.docs.forEach((relationshipDoc) => {
      if (seen.has(relationshipDoc.id)) return;
      seen.add(relationshipDoc.id);
      addRelationship(relationshipDoc.data());
    });
  };

  const results = await Promise.allSettled([
    run(
      query(
        collection(db, "relationships"),
        where("requesterId", "==", currentUid),
      ),
    ),
    run(
      query(
        collection(db, "relationships"),
        where("recipientId", "==", currentUid),
      ),
    ),
    run(
      query(
        collection(db, "relationships"),
        where("participantIds", "array-contains", currentUid),
      ),
    ),
  ]);

  if (results.every((result) => result.status === "rejected")) {
    throw new Error("All crew relationship visibility queries failed");
  }

  visibilityCache.set(currentUid, {
    visibleTo,
    expiresAt: nowMs() + CREW_VISIBILITY_CACHE_MS,
  });

  presenceDebug("refreshed accepted crew visibility", {
    uid: currentUid,
    visibleToIds: Object.keys(visibleTo),
    relationshipDocsSeen: seen.size,
  });

  return visibleTo;
}

export function invalidateCrewPresenceVisibility(uid: string): void {
  const cleanUid = String(uid || "").trim();
  if (cleanUid) visibilityCache.delete(cleanUid);
}

export async function updateOwnCrewPresence(params: {
  uid: string;
  displayName?: string;
  avatarUrl?: string;
  lat?: number | null;
  lng?: number | null;
  shareWithCrew: boolean;
  isOnline?: boolean;
  force?: boolean;
  refreshVisibility?: boolean;
  reason?: string;
}): Promise<void> {
  const uid = String(params.uid || "").trim();
  if (!uid) return;

  const lat = Number(params.lat);
  const lng = Number(params.lng);
  const hasLocation = Number.isFinite(lat) && Number.isFinite(lng);
  const sharing = params.shareWithCrew === true && hasLocation;
  const isOnline = sharing && params.isOnline !== false;
  const displayName = String(params.displayName || "Angler");
  const avatarUrl = String(params.avatarUrl || "");

  if (!sharing) {
    await markOwnCrewPresenceOffline(uid, {
      force: params.force,
      reason: params.reason,
    });
    return;
  }

  await enqueueCrewPresenceWrite(uid, async () => {
    const now = nowMs();
    let visibleTo: Record<string, boolean>;
    try {
      visibleTo = await resolveAcceptedCrewVisibleTo(
        uid,
        params.refreshVisibility === true,
      );
    } catch (error) {
      console.warn(
        "[crewPresence] could not resolve accepted crew visibility",
        error,
      );
      return;
    }

    const nextVisibilityKey = visibilityKey(visibleTo);
    const nextIdentityKey = `${displayName}|${avatarUrl}`;
    const previous = lastWriteByUid.get(uid);
    const movedEnough =
      !previous ||
      previous.lat == null ||
      previous.lng == null ||
      distanceMeters(previous.lat, previous.lng, lat, lng) >=
        CREW_PRESENCE_MIN_MOVE_METERS;
    const visibilityChanged =
      !previous || previous.visibilityKey !== nextVisibilityKey;
    const identityChanged =
      !previous || previous.identityKey !== nextIdentityKey;
    const onlineChanged = !previous || previous.online !== isOnline;

    if (
      !params.force &&
      !movedEnough &&
      !visibilityChanged &&
      !identityChanged &&
      !onlineChanged
    ) {
      presenceDebug("skipped unchanged presence write", {
        uid,
        reason: params.reason,
        ageMs: previous ? now - previous.at : null,
      });
      return;
    }

    await setDoc(
      doc(db, "userPresence", uid),
      {
        uid,
        displayName,
        avatarUrl,
        isOnline,
        online: isOnline,
        locationSharing: "crew",
        approxLocation: { lat, lng },
        visibleTo,
        visibleToIds: Object.keys(visibleTo).filter(Boolean).sort(),
        lastSeenAt: now,
        // Legacy compatibility field. This records the latest real lifecycle/
        // location publish; Roddo does not run a recurring presence heartbeat.
        heartbeatAt: now,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    lastWriteByUid.set(uid, {
      at: now,
      lat,
      lng,
      online: isOnline,
      visibilityKey: nextVisibilityKey,
      identityKey: nextIdentityKey,
    });

    presenceDebug("wrote presence", {
      uid,
      reason: params.reason,
      movedEnough,
      visibilityChanged,
      identityChanged,
      visibleToCount: Object.keys(visibleTo).length,
    });
  });
}

export async function markOwnCrewPresenceOffline(
  uid: string,
  options?: { force?: boolean; reason?: string },
): Promise<void> {
  const cleanUid = String(uid || "").trim();
  if (!cleanUid) return;

  await enqueueCrewPresenceWrite(cleanUid, async () => {
    const previous = lastWriteByUid.get(cleanUid);
    if (!options?.force && previous?.online === false) return;

    const now = nowMs();
    await setDoc(
      doc(db, "userPresence", cleanUid),
      {
        uid: cleanUid,
        isOnline: false,
        online: false,
        locationSharing: "off",
        approxLocation: null,
        // Preserve visibleTo / visibleToIds while offline. Those fields are the
        // document read ACL for accepted crewmates. Clearing them would revoke an
        // active listener and surface a Firestore permission error instead of an
        // ordinary offline update. No location is exposed because sharing is off
        // and approxLocation is null.
        lastSeenAt: now,
        // Legacy compatibility field; updated only on real lifecycle changes.
        heartbeatAt: now,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );

    lastWriteByUid.set(cleanUid, {
      at: now,
      lat: null,
      lng: null,
      online: false,
      visibilityKey: previous?.visibilityKey || "",
      identityKey: previous?.identityKey || "",
    });

    presenceDebug("wrote offline presence", {
      uid: cleanUid,
      reason: options?.reason,
    });
  });
}

export function subscribeToAcceptedCrewPresence(
  uid: string,
  onChange: (crew: CrewPresence[]) => void,
  onError?: (error: unknown) => void,
): Unsubscribe {
  const currentUid = String(uid || "").trim();
  if (!currentUid) {
    onChange([]);
    return () => {};
  }

  let cancelled = false;
  const relationshipBuckets = new Map<string, Set<string>>();
  const presenceRows = new Map<string, CrewPresence>();
  const presenceUnsubs = new Map<string, Unsubscribe>();

  const emitFreshRows = () => {
    if (cancelled) return;
    const cutoff = nowMs() - CREW_PRESENCE_STALE_MS;
    const freshRows = Array.from(presenceRows.values())
      .filter((presence) => presence.uid !== currentUid)
      .filter((presence) => presence.locationSharing === "crew")
      .filter((presence) => presence.isOnline === true)
      .filter((presence) => !!presence.approxLocation)
      .filter((presence) => {
        const activityAt = Math.max(
          safeNumber(presence.heartbeatAt, 0),
          safeNumber(presence.lastSeenAt, 0),
          safeNumber(presence.updatedAt, 0),
        );
        return activityAt >= cutoff;
      })
      .sort(
        (left, right) =>
          safeNumber(right.lastSeenAt, 0) - safeNumber(left.lastSeenAt, 0),
      );
    onChange(freshRows);
  };

  const desiredCrewIds = () => {
    const ids = new Set<string>();
    relationshipBuckets.forEach((bucket) => {
      bucket.forEach((id) => {
        if (id && id !== currentUid) ids.add(id);
      });
    });
    return ids;
  };

  const syncPresenceListeners = () => {
    if (cancelled) return;
    const desired = desiredCrewIds();

    for (const [crewUid, unsubscribe] of presenceUnsubs.entries()) {
      if (desired.has(crewUid)) continue;
      unsubscribe();
      presenceUnsubs.delete(crewUid);
      presenceRows.delete(crewUid);
    }

    desired.forEach((crewUid) => {
      if (presenceUnsubs.has(crewUid)) return;

      const unsubscribe = onSnapshot(
        doc(db, "userPresence", crewUid),
        (snapshot) => {
          if (cancelled) return;
          if (!snapshot.exists()) {
            presenceRows.delete(crewUid);
          } else {
            presenceRows.set(
              crewUid,
              normalizePresence(snapshot.id, snapshot.data()),
            );
          }
          emitFreshRows();
        },
        (error: any) => {
          const code = String(error?.code || '').toLowerCase();
          const message = String(error?.message || '').toLowerCase();
          const permissionDenied =
            code.includes('permission-denied') ||
            message.includes('missing or insufficient permissions');

          // Relationship state can change while an exact presence listener is
          // still attached (pending/removed/blocked). Firestore correctly revokes
          // that read. Treat it as a normal ACL transition: remove the stale row
          // and let the relationship streams detach the listener.
          if (!permissionDenied) {
            console.warn("[crewPresence] crew presence document failed", {
              currentUid,
              crewUid,
              error,
            });
            onError?.(error);
          } else {
            presenceDebug('presence listener access revoked', { currentUid, crewUid });
          }

          presenceRows.delete(crewUid);
          emitFreshRows();
        },
      );

      presenceUnsubs.set(crewUid, unsubscribe);
    });

    emitFreshRows();
  };

  const collectOtherIds = (snapshot: any) => {
    const ids = new Set<string>();
    snapshot.docs.forEach((relationshipDoc: any) => {
      const data = relationshipDoc.data();
      if (String(data?.status || "") !== "accepted") return;

      const requesterId = safeString(data?.requesterId).trim();
      const recipientId = safeString(data?.recipientId).trim();
      const participantIds = Array.isArray(data?.participantIds)
        ? data.participantIds
            .map((value: unknown) => String(value || "").trim())
            .filter(Boolean)
        : [];

      [...participantIds, requesterId, recipientId].forEach((id) => {
        if (id && id !== currentUid) ids.add(id);
      });
    });
    return ids;
  };

  // onSnapshot cannot fire when a crashed/force-killed client simply stops
  // writing. Re-evaluate the in-memory rows on a cheap local timer so stale
  // online records fall out without any polling reads.
  const staleRecheckTimer = window.setInterval(
    emitFreshRows,
    CREW_PRESENCE_STALE_RECHECK_MS,
  );

  const relationshipUnsubs: Unsubscribe[] = [];
  const attachRelationshipQuery = (
    key: string,
    relationshipQuery: ReturnType<typeof query>,
  ) => {
    relationshipUnsubs.push(
      onSnapshot(
        relationshipQuery,
        (snapshot) => {
          relationshipBuckets.set(key, collectOtherIds(snapshot));
          syncPresenceListeners();
        },
        (error) => {
          console.warn(
            `[crewPresence] relationship stream ${key} failed`,
            error,
          );
          relationshipBuckets.set(key, new Set());
          onError?.(error);
          syncPresenceListeners();
        },
      ),
    );
  };

  // These are the same two rule-compatible relationship queries already used
  // elsewhere in Roddo. Presence documents are then listened to by exact path,
  // so Firestore evaluates each read as a `get`, not a collection `list`.
  attachRelationshipQuery(
    "requesterId",
    query(
      collection(db, "relationships"),
      where("requesterId", "==", currentUid),
    ),
  );
  attachRelationshipQuery(
    "recipientId",
    query(
      collection(db, "relationships"),
      where("recipientId", "==", currentUid),
    ),
  );

  return () => {
    cancelled = true;
    window.clearInterval(staleRecheckTimer);
    relationshipUnsubs.forEach((unsubscribe) => unsubscribe());
    presenceUnsubs.forEach((unsubscribe) => unsubscribe());
    relationshipUnsubs.length = 0;
    presenceUnsubs.clear();
    presenceRows.clear();
  };
}
